import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { AgentRow } from '../transport/api'
import { agentKey, useLastTab } from '../agents/lastTab'
import { Icon } from '../ui/Icon'
import { TabShotSheet, said } from './TabShotSheet'
import { findHeld, refreshHeldTabs, tabsHeldBy, useHeldTabs, type Held } from './heldTabs'
import { tabPictureKey, useRetakeMoments, useTabPicture } from './useTabPicture'
import { tabRef, takeLeftPicture, useSheetOn } from './sheetPictures'
import { TabHandle, useTabTuck } from './tabTuck'

/** The corner is drawn at 30 % of the stage, between these (shell.css `.tab-corner`); its picture is asked for at that size. */
const CORNER_MIN_CSS_PX = 96
const CORNER_MAX_CSS_PX = 176
/** A focus counts as coming back only after the window was away this long (useRetakeMoments). */
const FOCUS_AWAY_MS = 30_000

/** The stage, in CSS pixels: the window less what is docked beside it (the rail, project files, the file browser). */
function stageWidth(): number {
  const cs = getComputedStyle(document.body)
  const px = (name: string) => parseFloat(cs.getPropertyValue(name)) || 0
  return window.innerWidth - px('--sb-w') - px('--pf-w') - px('--fs-dock')
}
function cornerFit(): number {
  const css = Math.min(CORNER_MAX_CSS_PX, Math.max(CORNER_MIN_CSS_PX, stageWidth() * 0.3))
  return Math.min(720, Math.max(320, Math.round(css * (window.devicePixelRatio || 1))))
}

type Pictured = { browserId: string; tab: string }
const isIt = (p: Pictured | null, h: Held | null) => !!p && !!h && p.browserId === h.browser.browser_id && p.tab === h.told.tab
const same = (a: Pictured | null, b: Pictured | null) => !!a && !!b && a.browserId === b.browserId && a.tab === b.tab
/** The tab the person looked at last in its sheet, and the tab the agent's commands had named last then. */
type Pick = Pictured & { agentHad: string | null }

/**
 * THE TAB AN AGENT HOLDS, IN THE CORNER OF ITS SESSION — owner, 2026-10-15: 如果 Agent 在某個
 * 分頁有活動, 也可以把該分頁的螢幕截圖縮得更小, 放到 Agent 的 Session 裡面, 讓使用者可以點擊,
 * 就跳到 App 裡面的那個 Chrome Tab 的分頁的螢幕截圖的那一個彈出視窗. Shown two ways (A: one
 * picture in the corner; B: one in the conversation for every step), he chose both; this is A:
 * 直接使用者點擊下去就可以跳到 More 的那一個頁面.
 *
 * Over the conversation and over the terminal alike, below the bar's buttons; App mounts one per
 * agent (keyed), so nothing of one session's corner is left in the next. A tap opens the sheet
 * More › Chrome tabs opens. Nothing at all when the agent holds no tab.
 *
 * TAKEN AT THREE MOMENTS, the ones the privacy policy names, and no others (the review of
 * 2026-10-15 counted five more): when the conversation is opened or comes back on screen, when
 * the agent finishes a turn, and when the person comes back to the app. Never while the
 * conversation is covered (`onScreen`: a phone's list over it, a sheet, the app hidden), and
 * never while a sheet pictures the same tab — closed, the sheet's last picture is shown here
 * instead of a new one. Each picture is a turn of the tab, which the agent's next command waits
 * behind, and one of the account's thirty a minute. So the picture is never fetched because it
 * is wanted (`auto: false`), only asked for by a moment: wanted, a corner whose first picture
 * was refused took one the instant a sheet closed or moved off its tab.
 *
 * WHICH TAB, when it holds several: the one the person looked at last in its sheet, until the
 * agent's own commands name another (owner, 2026-10-15: 可以設計讓使用者選擇預覽不同分頁的方法
 * … 在開啟分頁預覽彈出視窗, 上面顯示可以切換的 tab — the sheet has its tabs in a row at its top);
 * else the one its commands named last in this conversation (agents/lastTab.ts); else the first.
 * Chosen AT a moment, and when the sheet closes on another tab: then with the sheet's last
 * picture of it, and none taken here (no picture of it yet: the next moment takes one). An
 * agent moving between its tabs mid-turn moves nothing here until the turn ends; a tab that
 * leaves its hands gives way to the next by name, with its picture at the next moment.
 *
 * PUT AWAY by a swipe to the right (owner, 2026-11-01 — shell/tabTuck.tsx): a handle stays in its place, and nothing is
 * pictured while it is away — being brought back is an arrival, like coming back on screen.
 */
export function AgentTabCorner({ agent, onScreen }: { agent: AgentRow; onScreen: boolean }) {
  const qc = useQueryClient()
  const tuck = useTabTuck()
  const seen = onScreen && !tuck.away
  const list = useHeldTabs({ live: false })
  const all = list.data?.browsers ?? []
  const held = tabsHeldBy(all, agent)
  const lastTab = useLastTab(agentKey(agent))
  const [pick, setPick] = useState<Pick | null>(null)
  const picked = pick && pick.agentHad === lastTab ? pick : null
  // Over the first time the agent's commands name a tab after it — not back when they return to
  // the one they had named then (the review of 2026-10-15, second round).
  useEffect(() => {
    if (pick && pick.agentHad !== lastTab) setPick(null)
  }, [pick, lastTab])
  const choose = (from: Held[]) => (picked && from.find((h) => isIt(picked, h))) ?? from.find((h) => h.told.tab === lastTab) ?? from[0] ?? null

  const [pictured, setPictured] = useState<Pictured | null>(null)
  const inPicture = held.find((h) => isIt(pictured, h)) ?? null
  const shown = inPicture ?? choose(held)

  // Open, the sheet follows its tab by browser and label, whoever holds it now — as More's does.
  const [opened, setOpened] = useState<Held | null>(null)
  const openNow = opened && (findHeld(all, opened.browser.browser_id, opened.told.tab) ?? opened)
  const sheetOn = useSheetOn(pictured ? tabRef(pictured.browserId, pictured.tab) : null)
  const [fit] = useState(cornerFit)
  const may = seen && !opened && !sheetOn
  const { shot, takeAgain } = useTabPicture({
    browserId: pictured?.browserId ?? '',
    tab: pictured?.tab ?? '',
    fit,
    enabled: inPicture?.browser.shots === true && may,
    auto: false,
  })
  // A moment that chose another tab: its picture, once that tab is the one in the picture.
  const takeNext = useRef(false)
  useEffect(() => {
    if (!takeNext.current) return
    takeNext.current = false
    takeAgain()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per tab a moment chose
  }, [pictured])

  /*
   * A MOMENT: the list read again (it is not live here), the tab chosen, its picture taken. A new
   * tab in the picture is a new query, which takes the picture itself.
   */
  const now = useRef({ may, pictured, choose, takeAgain, held })
  now.current = { may, pictured, choose, takeAgain, held }
  const moment = async (reread: boolean) => {
    if (!now.current.may) return
    let from = now.current.held
    if (reread) {
      try {
        from = tabsHeldBy((await refreshHeldTabs(qc)).browsers, agent)
      } catch {
        /* the list as it was */
      }
    }
    const { may: still, pictured: was, choose: pick, takeAgain: again } = now.current
    const t = pick(from)
    if (!still || !t) return
    if (isIt(was, t)) again()
    else {
      takeNext.current = true
      setPictured({ browserId: t.browser.browser_id, tab: t.told.tab })
    }
  }

  // Opened, or back on screen: once per arrival — the list was just read on the first.
  const arrived = useRef<boolean | null>(null)
  const hasTab = !!shown
  useEffect(() => {
    if (!seen) {
      if (arrived.current) arrived.current = false
      return
    }
    if (arrived.current || !hasTab) return
    const first = arrived.current === null
    arrived.current = true
    void moment(!first)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- an arrival is the corner coming on screen, or the first tab appearing
  }, [seen, hasTab])

  useRetakeMoments({
    device: shown?.told.agent.device ?? agent.device_id ?? '',
    session: agent.session,
    focusAwayMs: FOCUS_AWAY_MS,
    onMoment: () => void moment(true),
  })

  // Closed on ANOTHER tab than the one in the picture: the person's pick, until the agent's
  // commands name another. A look at the same tab picks nothing.
  const lastSeen = useRef<Pictured | null>(null)
  const close = () => {
    if (opened) {
      const seen = { browserId: opened.browser.browser_id, tab: opened.told.tab }
      if (!same(seen, pictured)) {
        lastSeen.current = seen
        setPick({ ...seen, agentHad: lastTab })
      }
    }
    setOpened(null)
  }

  // A sheet that pictured this tab has closed: its last picture, not a new one — of the tab it
  // was closed on, when the person moved it to another of the agent's tabs.
  const covered = !!opened || sheetOn
  const wasCovered = useRef(false)
  useEffect(() => {
    const p = now.current.pictured
    const seen = lastSeen.current
    if (wasCovered.current && !covered) {
      lastSeen.current = null
      if (seen && !same(seen, p)) {
        const left = takeLeftPicture(tabRef(seen.browserId, seen.tab))
        if (left) {
          qc.setQueryData(tabPictureKey(seen.browserId, seen.tab, fit, false), { ok: true, image: left.image })
          setPictured(seen)
        }
      } else if (p) {
        const left = takeLeftPicture(tabRef(p.browserId, p.tab))
        if (left && left.at > shot.dataUpdatedAt) qc.setQueryData(tabPictureKey(p.browserId, p.tab, fit, false), { ok: true, image: left.image })
      }
    }
    wasCovered.current = covered
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per close
  }, [covered])

  if (!shown && !openNow) return null
  const name = shown ? shown.told.title || `Tab ${shown.told.tab}` : ''
  const others = held.length - 1
  const image = inPicture && inPicture === shown ? shot.data?.image : undefined
  const failed = shot.isError && inPicture === shown ? said(shot.error) : null
  return (
    <>
      {shown && tuck.away && <TabHandle label={`Browser tab: ${name}`} onOut={tuck.bringBack} />}
      {shown && !tuck.away && (
        <button
          type="button"
          className={`tab-corner${shown.told.paused ? ' paused' : ''}${failed && image ? ' stale' : ''}${tuck.back ? ' back' : ''}`}
          {...tuck.swipe}
          onClick={() => {
            if (tuck.tapped()) setOpened(shown)
          }}
          aria-haspopup="dialog"
          aria-label={`Open ${name}${shown.told.paused ? ', paused' : ''}${others > 0 ? `, ${others} more tab${others === 1 ? '' : 's'}` : ''}`}
          title={failed ? `${name}\nNo new picture: ${failed}` : name}
        >
          {/* An extension that takes no picture still shows which tab, and opens it (the sheet says why). */}
          {shown.browser.shots === true && (
            <span className="tab-corner-pic">
              {image ? <img src={`data:${image.mime};base64,${image.base64}`} alt="" /> : <Icon name="tab" size={18} />}
            </span>
          )}
          <span className="tab-corner-name">
            <span className={`art-dot ${shown.told.paused ? 'art-wait' : 'art-ok'}`} aria-hidden="true" />
            <span className="tab-corner-title">{name}</span>
            {others > 0 && <span className="tab-corner-more">+{others}</span>}
          </span>
        </button>
      )}
      {openNow && <TabShotSheet browser={openNow.browser} told={openNow.told} onSwitch={setOpened} onClose={close} />}
    </>
  )
}
